/* eslint-disable @nx/enforce-module-boundaries */
import { TreeViewProvider } from '@aibees/elephant/context/tree-view/provider';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { TextEllipsis } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Stack } from '@mui/material';
import useHover from 'ahooks/lib/useHover';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  ClearButtonType,
  ClearTagsButton,
} from '../../components/filter-tags/components/clear-tags-button';
import { useFilterTags } from '../../components/filter-tags/hooks/use-filter-tags';
import {
  buildClearAllDraft,
  getTagsCount,
} from '../../components/filter-tags/utils';
import { SearchFilter } from '../../types';
import { AutocompleteTreeFilterContent } from './autocomplete-tree-filter-content';
import * as SX from './sx';
import type { AutocompleteTreeFilterProps as Props } from './types';

export const AutocompleteTreeFilter = memo((props: Props) => {
  const { setParams, params } = useSearchQueryFilter({
    select: [props.field!],
  });
  const {
    onClearButtonClick: overrideOnClearButtonClick,
    showClearButton: overrideShowClearButton,
  } = props;
  const tags = useFilterTags(params, props as SearchFilter);
  const count = getTagsCount(tags);
  const hoverRef = useRef<HTMLDivElement>(null);
  const hover = useHover(hoverRef);
  const [selectedCustomValues] = useState<string[]>([]);

  const showClearButton = useMemo(
    () => overrideShowClearButton || (hover && !!count),
    [overrideShowClearButton, hover, count]
  );

  const handleClear = useCallback(() => {
    const draft = buildClearAllDraft(tags);
    setParams(draft);
  }, [tags, setParams]);

  const handleClearButtonClick = useCallback(() => {
    if (overrideOnClearButtonClick) {
      overrideOnClearButtonClick();
    } else {
      handleClear();
    }
  }, [overrideOnClearButtonClick, handleClear]);

  return (
    <Box
      className={classnames('AutocompleteTreeFilter', props.className)}
      sx={SX.getRootSx(props.metadata?.styles?.sx)}
      ref={hoverRef}
    >
      {props.name && (
        <Box sx={SX.header}>
          <TextEllipsis
            variant="body3"
            color="grey.600"
            fontWeight={600}
            value={props.name}
            lineHeight="24px"
            w={showClearButton ? '210px' : '240px'}
          />
          <Stack direction="row" gap={0.5}>
            {showClearButton && (
              <ClearTagsButton
                type={ClearButtonType.ICON_BUTTON}
                count={count || 1}
                onClick={handleClearButtonClick}
              />
            )}
          </Stack>
        </Box>
      )}
      <TreeViewProvider>
        <Box sx={SX.content}>
          <AutocompleteTreeFilterContent
            selectedCustomValues={selectedCustomValues}
            {...props}
          />
        </Box>
      </TreeViewProvider>
    </Box>
  );
});

AutocompleteTreeFilter.displayName = 'AutocompleteTreeFilter';
